// Os elementos de uma página, desenhados em HTML/CSS.
//
// Estes desenhos são compartilhados pelos motores `grade-css`, `fluxo-vertical` e `svg` — o
// motor decide o ARRANJO da página; o elemento se desenha do mesmo jeito nos três. Sem isso,
// cada motor reimplementaria o balão de fala, e três balões diferentes é como o produto acaba
// com três verdes diferentes.

import * as React from "react";
import { Elemento, Balao as BalaoEl } from "../api/documento.types";

/** O balão de fala. Cresce com o texto quando o documento pede — é o T1.6. */
/**
 * O TAMANHO DE FONTE PADRAO, E ELE PASSOU A SER PROPORCIONAL A PAGINA.
 *
 * T2.85 do pedido: *"que visualizacao pessima, viu? A LETRA MIUDISSIMA."* — sobre a pagina que o
 * roteirizador acabou de montar.
 *
 * ── A MEDICAO, feita em 2026-09-12 contra o backend em producao ─────────────────────────────
 *
 * Mandei a historia de prova para `POST /roteirizar` e olhei o documento que voltou. Duas coisas:
 *
 *   1. o roteirizador monta 2 paginas `tirinha` de 1200x400, com 6 baloes — ou seja, o T2.84
 *      ("montou uma pagina aqui que e so o titulo") NAO REPRODUZ MAIS, e quem o consertou foi a
 *      quebra por LLM de 12/09;
 *   2. **nenhum balao traz `tamanhoFonte`.** Todos voltam sem o campo.
 *
 * E o padrao daqui era `15` — quinze pixels em COORDENADA DE PAGINA. A pagina de 1200px e
 * escalada para caber na largura disponivel (860px na tela do roteirizador), o que da escala
 * 0,717. Quinze vezes 0,717 e **10,8 pixels na tela**.
 *
 * Dez pixels e menor que o minimo de qualquer diretriz de legibilidade, e e exatamente
 * "miudissima". O defeito nao estava no roteirizador nem na escala: estava em um numero FIXO
 * atravessando uma transformacao proporcional.
 *
 * ── POR QUE PROPORCIONAL, E POR QUE 2,5% ────────────────────────────────────────────────────
 *
 * Um valor fixo em coordenada de pagina nao pode estar certo para todas: a mesma "15" fica
 * enorme numa capa de 600px e invisivel num webtoon de 800x4000. O que e estavel entre formatos
 * e a PROPORCAO — quanto a letra ocupa da largura da folha.
 *
 * 2,5% da largura da, na tirinha de 1200px, 30px em coordenada de pagina e ~21px na tela. Numa
 * A4 de 1240px, 31px e ~21px na tela. O numero na TELA fica estavel entre formatos, que e o que
 * a pessoa de fato le.
 *
 * O piso de 14px em coordenada de pagina existe para formatos muito estreitos; o teto de 48px,
 * para nao transformar o balao de uma capa grande em cartaz.
 *
 * E `tamanhoFonte` EXPLICITO continua ganhando de tudo isto: quem escolheu um tamanho no editor
 * escolheu, e o padrao so vale para quem nao escolheu.
 */
export function tamanhoDeFontePadrao(larguraDaPagina: number, estilo?: string): number {
  const proporcional = Math.round(larguraDaPagina * 0.025);
  const base = Math.min(Math.max(proporcional, 14), 48);
  // Narracao e legenda, nao fala: um pouco menor, como era antes (13 contra 15).
  return estilo === "narracao" ? Math.round(base * 0.87) : base;
}

/**
 * O RECHEIO DE CADA ESTILO DE BALAO, EM `em` — T9.9.
 *
 * Duas coisas moram neste número, e as duas foram medidas.
 *
 * ── 1 · A ELIPSE PEDE MAIS QUE O RETANGULO ──────────────────────────────────────────────────
 *
 * O balão de pensamento é desenhado com `border-radius: 50% / 40%`, que é uma ELIPSE — e o texto
 * dentro dele continua sendo um RETÂNGULO. Nos cantos, o retângulo sai de dentro da elipse, e o
 * que se vê é a letra atravessando o contorno.
 *
 * Medido em 02/10/2026, no exemplo que a própria plataforma usa para mostrar como fica um
 * quadrinho pronto: *"a última linha sai por baixo e pela esquerda do contorno e atravessa a
 * borda do quadro"*. A conta fecha. Numa caixa de 300 por 154 com raio 50%/40%, o contorno no x
 * da primeira letra passa em y=34, e o texto começava em y=12.
 *
 * O maior retângulo que cabe numa elipse tem cada lado dividido por raiz de dois — sobra ~15% de
 * cada dimensão em cada lado. Daí o recheio horizontal quase o dobro do vertical: a elipse é
 * mais larga do que alta, e é na largura que a letra escapava.
 *
 * ── 2 · E O NUMERO E EM `em` PELO MESMO MOTIVO DO TAMANHO DE FONTE ──────────────────────────
 *
 * Era `12px 16px`, em COORDENADA DE PÁGINA — a mesma armadilha que o `tamanhoDeFontePadrao`
 * logo acima descreve: um número fixo atravessando uma transformação proporcional fica certo
 * numa tirinha de 1200 e errado numa capa de 600. Em `em`, ele acompanha a letra, que já é
 * proporcional à folha.
 */
export function recheioDoBalao(estilo?: string): string {
  if (estilo === "narracao") return "0.32em 0.52em";
  if (estilo === "pensamento") return "0.8em 1.5em";
  return "0.45em 0.62em";
}

function Balao({ el, larguraDaPagina }: { el: BalaoEl; larguraDaPagina: number }) {
  const cresce = el.crescePorTexto !== false;

  // ── O RECHEIO ACOMPANHA A LETRA, E A ELIPSE PEDE MAIS QUE O RETANGULO (T9.9) ──────────────
  //
  // O balão de pensamento é desenhado com `border-radius: 50% / 40%`, que é uma ELIPSE — e o
  // texto dentro dele continua sendo um RETÂNGULO. Nos cantos, o retângulo sai de dentro da
  // elipse, e o que se vê é a letra atravessando o contorno.
  //
  // Medido em 02/10/2026, no exemplo que a própria plataforma usa para mostrar como fica um
  // quadrinho pronto — *"a última linha sai por baixo e pela esquerda do contorno e atravessa a
  // borda do quadro"*. A conta fecha: numa caixa de 300 por 154 com raio 50%/40%, o contorno no
  // x da primeira letra passa em y=34, e o texto começava em y=12.
  //
  // E o recheio passa a ser em `em` e não em pixels: ele é medido em COORDENADA DE PÁGINA, a
  // mesma armadilha do `tamanhoDeFontePadrao` logo acima — um número fixo fica certo numa
  // tirinha de 1200 e errado numa capa de 600. Em `em`, ele acompanha a letra, que já é
  // proporcional à folha.
  const recheio = recheioDoBalao(el.estilo);

  const formato: React.CSSProperties =
    el.estilo === "pensamento"
      ? { borderRadius: "50% / 40%" }
      : el.estilo === "grito"
      ? { borderRadius: 6 }
      : el.estilo === "narracao"
      ? { borderRadius: 4 }
      : { borderRadius: 26 };

  return (
    <div
      style={{
        position: "absolute",
        left: el.x,
        top: el.y,
        // Com `crescePorTexto`, a largura declarada vira LIMITE e a altura fica automática:
        // o balão acompanha o texto. Sem ele, respeita o tamanho gravado.
        width: cresce ? "auto" : el.largura,
        maxWidth: cresce ? el.largura : undefined,
        height: cresce ? "auto" : el.altura,
        background: el.estilo === "narracao" ? "var(--hq-superficie)" : "#fff",
        color: el.corTexto ?? "var(--hq-tinta)",
        border: `3px ${el.estilo === "sussurro" ? "dashed" : "solid"} var(--hq-contorno)`,
        padding: recheio,
        fontFamily: el.fonte ?? "var(--hq-fonte-texto)",
        fontSize: el.tamanhoFonte ?? tamanhoDeFontePadrao(larguraDaPagina, el.estilo),
        fontWeight: el.estilo === "grito" ? 700 : 500,
        fontStyle: el.estilo === "narracao" ? "italic" : "normal",
        lineHeight: 1.35,
        overflowWrap: "anywhere",
        boxSizing: "border-box",
        ...formato,
      }}
    >
      {el.texto}
    </div>
  );
}

/** Um elemento qualquer, posicionado em coordenadas da página. */
export function DesenharElemento({
  el,
  larguraDaPagina,
}: {
  el: Elemento;
  /** A largura da folha em coordenada de pagina. O tamanho de fonte padrao sai daqui. */
  larguraDaPagina: number;
}) {
  if (el.visivel === false) return null;

  const caixa: React.CSSProperties = {
    position: "absolute",
    left: el.x,
    top: el.y,
    width: el.largura,
    height: el.altura,
    transform: el.rotacao ? `rotate(${el.rotacao}deg)` : undefined,
    boxSizing: "border-box",
  };

  switch (el.tipo) {
    case "quadro":
      return (
        <div
          style={{
            ...caixa,
            border: `${el.traco ?? 4}px solid ${el.corTraco ?? "var(--hq-contorno)"}`,
            borderRadius: el.raio ?? 6,
            background: el.corFundo ?? "var(--hq-fundo-suave)",
          }}
        />
      );

    case "imagem":
      return (
        <img
          src={el.refImagem}
          // A descrição acessível é do documento; sem ela a imagem fica decorativa em vez de
          // anunciar um nome de arquivo para quem usa leitor de tela.
          alt={el.descricaoAcessivel ?? ""}
          style={{ ...caixa, objectFit: "cover" }}
        />
      );

    case "balao":
      return <Balao el={el} larguraDaPagina={larguraDaPagina} />;

    case "texto":
      return (
        <div
          style={{
            ...caixa,
            height: "auto",
            fontFamily: el.fonte ?? "var(--hq-fonte-texto)",
            // Mesmo defeito, mesma correcao: 16 fixo virava ~11px na tela depois da escala.
            fontSize: el.tamanhoFonte ?? tamanhoDeFontePadrao(larguraDaPagina),
            fontWeight: el.peso ?? 500,
            color: el.cor ?? "var(--hq-tinta)",
            textAlign:
              el.alinhamento === "centro" ? "center" : el.alinhamento === "direita" ? "right" : "left",
            overflowWrap: "anywhere",
          }}
        >
          {el.texto}
        </div>
      );

    case "forma": {
      const comum = {
        ...caixa,
        background: el.corPreenchimento ?? "var(--hq-fundo-suave)",
        border: `${el.traco ?? 3}px solid ${el.corTraco ?? "var(--hq-contorno)"}`,
      };
      if (el.forma === "elipse") return <div style={{ ...comum, borderRadius: "50%" }} />;
      if (el.forma === "linha") {
        return (
          <div
            style={{
              ...caixa,
              height: el.traco ?? 3,
              background: el.corTraco ?? "var(--hq-contorno)",
              border: "none",
            }}
          />
        );
      }
      return <div style={comum} />;
    }

    case "personagem":
      // O acervo de poses ainda não está no produto: o personagem entra como espaço reservado
      // NOMEADO. Um retângulo cinza sem nome esconde a lacuna; com o nome, quem abre a página
      // sabe quem deveria estar ali.
      return (
        <div
          style={{
            ...caixa,
            border: "3px dashed var(--hq-tinta-fraca)",
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "var(--hq-fonte-texto)",
            // O MENOR TEXTO DA PAGINA, e era o menor de todos. Medido em 2026-09-12 na tela de
            // motores: 12px em coordenada de pagina, escala 0,694 -> 8,3px na TELA. Pior que os
            // baloes antes do conserto do T2.85. Mesmo defeito, mesma correcao: proporcional a
            // folha, e nao numero fixo atravessando uma transformacao proporcional.
            //
            // `0.8` porque isto e um ROTULO de espaco reservado, e nao fala da historia: ele
            // precisa ser legivel sem competir com o balao ao lado.
            fontSize: Math.round(tamanhoDeFontePadrao(larguraDaPagina) * 0.8),
            color: "var(--hq-tinta-fraca)",
            textTransform: "capitalize",
            transform: `${el.rotacao ? `rotate(${el.rotacao}deg) ` : ""}${el.espelhado ? "scaleX(-1)" : ""}`.trim() || undefined,
          }}
        >
          {el.personagem}
        </div>
      );

    default:
      // União fechada: se um tipo novo entrar no esquema e não for tratado aqui, o TypeScript
      // acusa em tempo de compilação em vez de a página renderizar um buraco.
      return null;
  }
}
